import { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, UserPlus, Eye, EyeOff, Check, X } from "lucide-react";
import { toast } from "sonner";
import Nav from "@/components/Nav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatApiError } from "@/context/AuthContext";
import { REGISTER_API } from "@/context/Api";
import LmsCountryCode from "./LmsCountryCode"; // adjust path to wherever you keep this file

// Regex patterns for validation
const NAME_REGEX = /^[A-Za-z\s]+$/; // only letters and spaces, no special chars
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/; // must contain @ and a valid domain

const SignUp = () => {
  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");

  const [organization, setOrganization] = useState("");
  const [designation, setDesignation] = useState("");
  const [phone, setPhone] = useState("");
  const [countryCode, setCountryCode] = useState("IN"); // default country for the phone field
  const [city, setCity] = useState("");
  const [stateName, setStateName] = useState("");
  const [country, setCountry] = useState("");
  // const [region, setRegion] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const validate = ({
    trimmedName,
    trimmedEmail,
    trimmedPhone,
    trimmedOrganization,
    trimmedDesignation,
    trimmedCity,
    trimmedState,
    trimmedCountry,
  }) => {
    const newErrors = {};

    // Full Name: required, no special characters
    if (!trimmedName) {
      newErrors.name = "Full name is required.";
    } else if (!NAME_REGEX.test(trimmedName)) {
      newErrors.name = "Name should not contain numbers or special characters.";
    }

    // Email: required, must be a valid email with @
    if (!trimmedEmail) {
      newErrors.email = "Email is required.";
    } else if (!EMAIL_REGEX.test(trimmedEmail)) {
      newErrors.email = "Please enter a valid email address.";
    }

    // Phone: required, only digits, minimum 10 digits
    const digitsOnly = trimmedPhone.replace(/\D/g, "");
    if (!trimmedPhone) {
      newErrors.phone = "Phone number is required.";
    } else if (digitsOnly.length < 10) {
      newErrors.phone = "Phone number must be at least 10 digits.";
    }

    // Organization: required, no special characters
    if (!trimmedOrganization) {
      newErrors.organization = "Organization is required.";
    } else if (!NAME_REGEX.test(trimmedOrganization)) {
      newErrors.organization = "Organization should not contain numbers or special characters.";
    }

    // Designation: required, no special characters
    if (!trimmedDesignation) {
      newErrors.designation = "Designation is required.";
    } else if (!NAME_REGEX.test(trimmedDesignation)) {
      newErrors.designation = "Designation should not contain numbers or special characters.";
    }

    // City: required, no special characters
    if (!trimmedCity) {
      newErrors.city = "City is required.";
    } else if (!NAME_REGEX.test(trimmedCity)) {
      newErrors.city = "City should not contain numbers or special characters.";
    }

    // State: required, no special characters
    if (!trimmedState) {
      newErrors.state = "State is required.";
    } else if (!NAME_REGEX.test(trimmedState)) {
      newErrors.state = "State should not contain numbers or special characters.";
    }

    // Country: required, no special characters
    if (!trimmedCountry) {
      newErrors.country = "Country is required.";
    } else if (!NAME_REGEX.test(trimmedCountry)) {
      newErrors.country = "Country should not contain numbers or special characters.";
    }

    return newErrors;
  };

  const handleRegister = async (e) => {
    e.preventDefault();

    // Prevent multiple submit clicks
    if (submitting) return;

    const trimmedName = name.trim();
    const trimmedEmail = email.trim().toLowerCase();
    const trimmedOrganization = organization.trim();
    const trimmedDesignation = designation.trim();
    const trimmedPhone = phone.trim();
    const trimmedCity = city.trim();
    const trimmedState = stateName.trim();
    const trimmedCountry = country.trim();

    // Run validation before hitting the API
    const validationErrors = validate({
      trimmedName,
      trimmedEmail,
      trimmedPhone,
      trimmedOrganization,
      trimmedDesignation,
      trimmedCity,
      trimmedState,
      trimmedCountry,
    });

    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      toast.error("Please fix the highlighted fields.");
      return;
    }

    setSubmitting(true);

    try {
      await REGISTER_API({
        name: trimmedName,
        email: trimmedEmail,
        organization: trimmedOrganization,
        designation: trimmedDesignation,
        phone: trimmedPhone,
        region: countryCode,
        city: trimmedCity,
        state: trimmedState,
        country: trimmedCountry,
      });

      toast.success(
        "Account created! Please check your email to confirm your account."
      );

      navigate("/login");
    } catch (err) {
      const apiMessage = err?.response?.data?.deatils;

      toast.error(
        formatApiError(apiMessage) ||
        formatApiError(err) ||
        "Could not create account."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const goToLogin = () => {
    navigate("/login");
  };

  useEffect(() => {
    const script = document.createElement("script");

    script.src =
      "https://cyberfrat.foctta.com/consent-form.js";

    script.setAttribute(
      "data-profile-id",
      "0845155e-b0d2-46a7-9fce-e9610112dc78"
    );

    script.setAttribute(
      "data-tenant",
      "5dcc62cd-1442-4324-a97f-8a8b617fe618"
    );

    script.setAttribute(
      "data-regulation",
      "DPDPA"
    );

    script.setAttribute("data-collection-context", "at_collection");
    script.setAttribute("data-lang", "en");

    script.async = true;

    document
      .getElementById("foctta-consent")
      ?.appendChild(script);

    return () => script.remove();
  }, []);

  return (
    <div className="min-h-screen bg-[#07070c] text-white font-body">
      <Nav />
      <div className="relative min-h-screen flex items-center justify-center px-4 sm:px-6 pt-24 pb-12 noise overflow-hidden">
        <div className="absolute inset-0 grid-lines opacity-40" />
        <div className="absolute -top-40 right-0 h-[420px] w-[420px] rounded-full blur-[130px] opacity-40 bg-[#4d4479]" />

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          data-testid="signup-card"
          className="relative z-10 w-full max-w-3xl mx-auto rounded-2xl border border-[#3d414f] bg-[#0a0a12]/90 backdrop-blur-xl p-6 sm:p-8 md:p-10"
        >
          <div className="flex items-center gap-3 mb-2">
            <span className="grid h-10 w-10 place-items-center rounded-full bg-[#16fcfb] text-[#07070c]">
              <UserPlus size={18} />
            </span>
            <span className="font-mono2 text-xs uppercase tracking-[0.25em] text-[#16fcfb]">Create Account</span>
          </div>
          <h1 className="font-display font-700 text-3xl text-white">Join hackTonomy</h1>
          <p className="mt-2 font-body text-sm text-white/60">Create your account to get started.</p>

          <form onSubmit={handleRegister} className="mt-7 space-y-4" data-testid="signup-form" noValidate>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="signup-name" className="font-mono2 text-[10px] uppercase tracking-[0.12em] text-[#97b8ff]">
                  Full Name
                </Label>
                <Input
                  id="signup-name"
                  data-testid="signup-name"
                  type="text"
                  autoComplete="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Jane Doe"
                  className={`bg-[#07070c] border-[#3d414f] text-white placeholder:text-white/30 focus-visible:ring-[#16fcfb] h-11 ${errors.name ? "border-red-500 focus-visible:ring-red-500" : ""
                    }`}
                />
                {errors.name && (
                  <p className="text-xs text-red-400 mt-1">{errors.name}</p>
                )}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="signup-email" className="font-mono2 text-[10px] uppercase tracking-[0.12em] text-[#97b8ff]">
                  Email
                </Label>
                <Input
                  id="signup-email"
                  data-testid="signup-email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  className={`bg-[#07070c] border-[#3d414f] text-white placeholder:text-white/30 focus-visible:ring-[#16fcfb] h-11 ${errors.email ? "border-red-500 focus-visible:ring-red-500" : ""
                    }`}
                />
                {errors.email && (
                  <p className="text-xs text-red-400 mt-1">{errors.email}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="signup-organization" className="font-mono2 text-[10px] uppercase tracking-[0.12em] text-[#97b8ff]">
                  Organization
                </Label>
                <Input
                  id="signup-organization"
                  data-testid="signup-organization"
                  type="text"
                  autoComplete="organization"
                  value={organization}
                  onChange={(e) => setOrganization(e.target.value)}
                  placeholder="Acme Corp"
                  className={`bg-[#07070c] border-[#3d414f] text-white placeholder:text-white/30 focus-visible:ring-[#16fcfb] h-11 ${errors.organization ? "border-red-500 focus-visible:ring-red-500" : ""
                    }`}
                />
                {errors.organization && (
                  <p className="text-xs text-red-400 mt-1">{errors.organization}</p>
                )}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="signup-designation" className="font-mono2 text-[10px] uppercase tracking-[0.12em] text-[#97b8ff]">
                  Designation
                </Label>
                <Input
                  id="signup-designation"
                  data-testid="signup-designation"
                  type="text"
                  autoComplete="organization-title"
                  value={designation}
                  onChange={(e) => setDesignation(e.target.value)}
                  placeholder="Security Analyst"
                  className={`bg-[#07070c] border-[#3d414f] text-white placeholder:text-white/30 focus-visible:ring-[#16fcfb] h-11 ${errors.designation ? "border-red-500 focus-visible:ring-red-500" : ""
                    }`}
                />
                {errors.designation && (
                  <p className="text-xs text-red-400 mt-1">{errors.designation}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="signup-phone" className="font-mono2 text-[10px] uppercase tracking-[0.12em] text-[#97b8ff]">
                  Phone
                </Label>
                <LmsCountryCode
                  country={countryCode}
                  contact={phone}
                  set_country_code={setCountryCode}
                  set_contact_no={setPhone}
                />
                {errors.phone && (
                  <p className="text-xs text-red-400 mt-1">{errors.phone}</p>
                )}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="signup-city" className="font-mono2 text-[10px] uppercase tracking-[0.12em] text-[#97b8ff]">
                  City
                </Label>
                <Input
                  id="signup-city"
                  data-testid="signup-city"
                  type="text"
                  autoComplete="address-level2"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  placeholder="Jaipur"
                  className={`bg-[#07070c] border-[#3d414f] text-white placeholder:text-white/30 focus-visible:ring-[#16fcfb] h-11 ${errors.city ? "border-red-500 focus-visible:ring-red-500" : ""
                    }`}
                />
                {errors.city && (
                  <p className="text-xs text-red-400 mt-1">{errors.city}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="signup-state" className="font-mono2 text-[10px] uppercase tracking-[0.12em] text-[#97b8ff]">
                  State
                </Label>
                <Input
                  id="signup-state"
                  data-testid="signup-state"
                  type="text"
                  autoComplete="address-level1"
                  value={stateName}
                  onChange={(e) => setStateName(e.target.value)}
                  placeholder="Rajasthan"
                  className={`bg-[#07070c] border-[#3d414f] text-white placeholder:text-white/30 focus-visible:ring-[#16fcfb] h-11 ${errors.state ? "border-red-500 focus-visible:ring-red-500" : ""
                    }`}
                />
                {errors.state && (
                  <p className="text-xs text-red-400 mt-1">{errors.state}</p>
                )}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="signup-country" className="font-mono2 text-[10px] uppercase tracking-[0.12em] text-[#97b8ff]">
                  Country
                </Label>
                <Input
                  id="signup-country"
                  data-testid="signup-country"
                  type="text"
                  autoComplete="country-name"
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  placeholder="India"
                  className={`bg-[#07070c] border-[#3d414f] text-white placeholder:text-white/30 focus-visible:ring-[#16fcfb] h-11 ${errors.country ? "border-red-500 focus-visible:ring-red-500" : ""
                    }`}
                />
                {errors.country && (
                  <p className="text-xs text-red-400 mt-1">{errors.country}</p>
                )}
              </div>
            </div>

            <div id="foctta-consent" ></div>

            <Button
              type="submit"
              data-testid="signup-submit-btn"
              disabled={submitting}
              className="w-full rounded-full h-12 bg-[#16fcfb] text-[#07070c] hover:bg-[#97b8ff] font-mono2 text-xs uppercase tracking-widest glow-cyan"
            >
              {submitting ? (
                <>
                  <Loader2 size={16} className="mr-2 animate-spin" /> Creating account
                </>
              ) : (
                "Create Account"
              )}
            </Button>

            <Button
              type="button"
              data-testid="signup-goto-login-btn"
              onClick={goToLogin}
              variant="outline"
              className="w-full rounded-full h-12 border-[#3d414f] bg-transparent text-white hover:bg-[#16fcfb]/10 hover:border-[#16fcfb] font-mono2 text-xs uppercase tracking-widest"
            >
              Sign In
            </Button>
          </form>

          <p className="mt-6 text-center font-body text-sm text-white/55">
            Already have an account?{" "}
            <Link to="/login" className="text-[#16fcfb] hover:underline">
              Sign in here
            </Link>
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default SignUp;